import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Autocomplete,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  sendPushNotificationToAll,
  sendPushNotificationToMember,
  sendPushNotificationToMembers,
  sendPushNotificationToGroup,
  sendPushNotificationToGroups,
  sendPushNotificationToDevice,
  testDeviceToken,
} from '../../../controllers/pushNotificationController';
import { searchMembers } from '../../../controllers/memberManagementController';
import { searchGroups } from '../../../controllers/groupManagementController';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import { Avatar } from '@mui/material';

const PushNotificationSendTab = () => {
  const [mode, setMode] = useState('member'); // member, group, groups, device
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  // Common fields
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dataPairs, setDataPairs] = useState([{ key: '', value: '' }]);
  const [source, setSource] = useState('');
  const [sourceId, setSourceId] = useState('');

  // Member mode - unified for all, single, and multiple
  const [membersQuery, setMembersQuery] = useState('');
  const [membersOptions, setMembersOptions] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Group mode - unified for single and multiple
  const [groupsQuery, setGroupsQuery] = useState('');
  const [groupsOptions, setGroupsOptions] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  // Device mode
  const [deviceToken, setDeviceToken] = useState('');
  const [deviceType, setDeviceType] = useState(0); // 0: iOS, 1: Android
  const [testTokenResult, setTestTokenResult] = useState(null);
  const [testTokenDialogOpen, setTestTokenDialogOpen] = useState(false);
  const [testingToken, setTestingToken] = useState(false);

  // Search members for member mode
  useEffect(() => {
    if (mode !== 'member' || !membersQuery || membersQuery.length < 2) {
      setMembersOptions([]);
      return;
    }
    let active = true;
    setMembersLoading(true);
    searchMembers(membersQuery)
      .then((data) => {
        if (active) setMembersOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setMembersOptions([]);
      })
      .finally(() => {
        if (active) setMembersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, membersQuery]);

  // Search groups for group mode
  useEffect(() => {
    if (mode !== 'groups' || !groupsQuery || groupsQuery.length < 2) {
      setGroupsOptions([]);
      return;
    }
    let active = true;
    setGroupsLoading(true);
    searchGroups({ keyword: groupsQuery })
      .then((data) => {
        if (active) setGroupsOptions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setGroupsOptions([]);
      })
      .finally(() => {
        if (active) setGroupsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mode, groupsQuery]);

  const handleAddDataPair = () => {
    setDataPairs([...dataPairs, { key: '', value: '' }]);
  };

  const handleRemoveDataPair = (index) => {
    setDataPairs(dataPairs.filter((_, i) => i !== index));
  };

  const handleDataPairChange = (index, field, value) => {
    const updated = [...dataPairs];
    updated[index][field] = value;
    setDataPairs(updated);
  };

  const buildDataObject = () => {
    const data = {};
    dataPairs.forEach((pair) => {
      if (pair.key && pair.value) {
        data[pair.key] = pair.value;
      }
    });
    return Object.keys(data).length > 0 ? data : null;
  };

  const validateForm = () => {
    if (!title.trim()) {
      setSendError('Başlık alanı boş olamaz.');
      return false;
    }
    if (!body.trim()) {
      setSendError('İçerik alanı boş olamaz.');
      return false;
    }
    if (title.length > 64) {
      setSendError('Başlık en fazla 64 karakter olabilir.');
      return false;
    }
    if (body.length > 178) {
      setSendError('İçerik en fazla 178 karakter olabilir.');
      return false;
    }

    if (mode === 'groups' && selectedGroups.length === 0) {
      setSendError('En az bir grup seçmelisiniz.');
      return false;
    }
    if (mode === 'device') {
      if (!deviceToken.trim()) {
        setSendError('Device token alanı boş olamaz.');
        return false;
      }
      if (deviceToken.length > 500) {
        setSendError('Device token en fazla 500 karakter olabilir.');
        return false;
      }
    }

    return true;
  };

  const handleSend = async () => {
    if (!validateForm()) return;

    setSending(true);
    setSendError('');
    setSendSuccess('');

    try {
      const data = buildDataObject();
      const payload = {
        title: title.trim(),
        body: body.trim(),
        data,
        source: source.trim() || null,
        sourceId: sourceId.trim() ? sourceId.trim() : null,
      };

      let result;
      switch (mode) {
        case 'member':
          // Üye seçimine göre gönderim yap
          if (selectedMembers.length === 0) {
            // Hiç üye seçilmemişse tüm üyelere gönder
            result = await sendPushNotificationToAll(payload);
          } else if (selectedMembers.length === 1) {
            // Bir üye seçilmişse tek üyeye gönder
            result = await sendPushNotificationToMember({
              ...payload,
              memberId: selectedMembers[0].id || selectedMembers[0].Id,
            });
          } else {
            // Birden fazla üye seçilmişse birden fazla üyeye gönder
            result = await sendPushNotificationToMembers({
              ...payload,
              memberIds: selectedMembers.map((m) => m.id || m.Id),
            });
          }
          break;
        case 'groups':
          // Grup seçimine göre gönderim yap
          if (selectedGroups.length === 1) {
            // Bir grup seçilmişse tek gruba gönder
            result = await sendPushNotificationToGroup({
              ...payload,
              groupId: selectedGroups[0].id || selectedGroups[0].Id,
            });
          } else {
            // Birden fazla grup seçilmişse birden fazla gruba gönder
            result = await sendPushNotificationToGroups({
              ...payload,
              groupIds: selectedGroups.map((g) => g.id || g.Id),
            });
          }
          break;
        case 'device':
          result = await sendPushNotificationToDevice({
            ...payload,
            deviceToken: deviceToken.trim(),
            deviceType,
          });
          break;
        default:
          throw new Error('Geçersiz gönderim modu');
      }

      const successMessage =
        mode === 'device'
          ? 'Bildirim device token\'a gönderildi.'
          : `Bildirim gönderildi. Toplam: ${result.totalRecipients || 0}, Başarılı: ${result.successCount || 0}, Başarısız: ${result.failureCount || 0}`;

      setSendSuccess(successMessage);
      resetForm();
    } catch (error) {
      setSendError(error?.message || 'Bildirim gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setBody('');
    setDataPairs([{ key: '', value: '' }]);
    setSource('');
    setSourceId('');
    setSelectedMembers([]);
    setMembersQuery('');
    setSelectedGroups([]);
    setGroupsQuery('');
    setDeviceToken('');
    setDeviceType(0);
  };

  const handleTestToken = async () => {
    if (!deviceToken.trim()) {
      setSendError('Device token alanı boş olamaz.');
      return;
    }
    setTestingToken(true);
    setTestTokenResult(null);
    try {
      const result = await testDeviceToken({
        deviceToken: deviceToken.trim(),
        deviceType,
      });
      setTestTokenResult(result);
      setTestTokenDialogOpen(true);
    } catch (error) {
      setSendError(error?.message || 'Token test edilemedi.');
    } finally {
      setTestingToken(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Tabs
        value={mode}
        onChange={(e, value) => {
          setMode(value);
          setSendError('');
          setSendSuccess('');
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            '&.Mui-selected': {
              color: '#0E4D94',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0E4D94',
          },
        }}
      >
        <Tab label="Üye Bildirimi" value="member" />
        <Tab label="Ocakbaşı Grubu Bildirimi" value="groups" />
        <Tab label="Device Token (Test)" value="device" />
      </Tabs>

      <Stack spacing={3}>

        {sendError && <Alert severity="error">{sendError}</Alert>}
        {sendSuccess && <Alert severity="success">{sendSuccess}</Alert>}

        {mode === 'groups' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Ocakbaşı Grubu Seç
            </Typography>
            <Autocomplete
              multiple
              options={groupsOptions}
              loading={groupsLoading}
              value={selectedGroups}
              onChange={(e, value) => setSelectedGroups(value)}
              onInputChange={(e, value) => setGroupsQuery(value)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => (option?.id || option?.Id) === (value?.id || value?.Id)}
              getOptionLabel={(option) => option?.name || ''}
              disabled={sending}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id || option.Id} label={option.name} />)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Grup adı yazın..."
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {groupsLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      padding: '5px!important',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              )}
            />
            {selectedGroups.length > 0 && (
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mt: 0.5 }}>
                {selectedGroups.length === 1
                  ? '1 grup seçildi (Tek gruba gönderilecek)'
                  : `${selectedGroups.length} grup seçildi (Seçili gruplara gönderilecek)`}
              </Typography>
            )}
          </Box>
        )}

        {mode === 'member' && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Üye Seç
            </Typography>
            <Autocomplete
              multiple
              options={membersOptions}
              loading={membersLoading}
              value={selectedMembers}
              onChange={(e, value) => setSelectedMembers(value)}
              onInputChange={(e, value) => setMembersQuery(value)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => (option?.id || option?.Id) === (value?.id || value?.Id)}
              getOptionLabel={(option) => option?.fullName || ''}
              disabled={sending}
              renderOption={(props, option) => (
                <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id || option.Id}>
                  <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 24, height: 24 }} />
                  <Typography>{option.fullName}</Typography>
                </Stack>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id || option.Id}
                    label={option.fullName}
                    avatar={<Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 20, height: 20 }} />}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Üye adı yazın... (Boş bırakılırsa tüm üyelere gönderilir)"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {membersLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '5px',
                      padding: '5px!important',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0E4D94',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.2,
                      px: 1.5,
                      fontSize: '14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e6e6e6',
                    },
                  }}
                />
              )}
            />
            {selectedMembers.length > 0 && (
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mt: 0.5 }}>
                {selectedMembers.length === 1
                  ? '1 üye seçildi (Tek üyeye gönderilecek)'
                  : `${selectedMembers.length} üye seçildi (Seçili üyelere gönderilecek)`}
              </Typography>
            )}
            {selectedMembers.length === 0 && (
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#666666', mt: 0.5 }}>
                Hiç üye seçilmedi (Tüm üyelere gönderilecek)
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
            Başlık
          </Typography>
          <TextField
            fullWidth
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder=""
            helperText={`${title.length}/64 karakter`}
            inputProps={{ maxLength: 64 }}
            disabled={sending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputBase-input': {
                py: 1.2,
                px: 1.5,
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e6e6e6',
              },
              '& .MuiFormHelperText-root': {
                ml: 0,
              },
            }}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
            İçerik
          </Typography>
          <TextField
            fullWidth
            required
            multiline
            minRows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder=""
            helperText={`${body.length}/178 karakter`}
            inputProps={{ maxLength: 178 }}
            disabled={sending}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                px: 0,
                py: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e6e6e6',
              },
              '& .MuiFormHelperText-root': {
                ml: 0,
              },
            }}
          />
        </Box>

        <Accordion
          sx={{
            boxShadow: 'none',
            backgroundColor: 'transparent',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#0E4D94' }} />}
            sx={{
              px: 0,
              py: 1,
              minHeight: 'auto',
              backgroundColor: 'transparent',
              '& .MuiAccordionSummary-content': {
                my: 0,
              },
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500, 
                fontSize: '14px',
                color: '#0E4D94',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Gelişmiş özellikler
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pb: 2, pt: 0, backgroundColor: 'transparent' }}>
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Ek Veri (Data) - Opsiyonel
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddDataPair}
                    disabled={sending}
                    sx={{
                      textTransform: 'none',
                      fontSize: '13px',
                      color: '#0E4D94',
                      '&:hover': {
                        backgroundColor: 'rgba(14, 77, 148, 0.04)',
                      },
                    }}
                  >
                    Ekle
                  </Button>
                </Stack>
                <Stack spacing={1.5}>
                  {dataPairs.map((pair, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={pair.key}
                        onChange={(e) => handleDataPairChange(index, 'key', e.target.value)}
                        placeholder="Key"
                        disabled={sending}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1,
                            px: 1.5,
                            fontSize: '14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        value={pair.value}
                        onChange={(e) => handleDataPairChange(index, 'value', e.target.value)}
                        placeholder="Value"
                        disabled={sending}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '5px',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#0E4D94',
                            },
                          },
                          '& .MuiInputBase-input': {
                            py: 1,
                            px: 1.5,
                            fontSize: '14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e6e6e6',
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveDataPair(index)}
                        disabled={dataPairs.length === 1 || sending}
                        sx={{
                          backgroundColor: '#FFEEF3',
                          borderRadius: '6px',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: '#FFD6E0',
                          },
                          '& svg': {
                            fontSize: '16px',
                            color: '#F8285A',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Kaynak (Source) - Opsiyonel
                  </Typography>
                  <TextField
                    fullWidth
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder=""
                    disabled={sending}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Kaynak ID (Source ID) - Opsiyonel
                  </Typography>
                  <TextField
                    fullWidth
                    value={sourceId}
                    onChange={(e) => setSourceId(e.target.value)}
                    placeholder=""
                    disabled={sending}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '5px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0E4D94',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: 1.2,
                        px: 1.5,
                        fontSize: '14px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e6e6e6',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {mode === 'device' && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Device Token (Expo Push Token)
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                value={deviceToken}
                onChange={(e) => setDeviceToken(e.target.value)}
                placeholder=""
                helperText="ExponentPushToken[xxxxx] formatında veya sadece token değeri"
                inputProps={{ maxLength: 500 }}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    px: 0,
                    py: 0,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>
            <Box sx={{ maxWidth: 200 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                Device Type
              </Typography>
              <TextField
                fullWidth
                select
                value={deviceType}
                onChange={(e) => setDeviceType(Number(e.target.value))}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                <MenuItem value={0}>iOS</MenuItem>
                <MenuItem value={1}>Android</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Button
                variant="outlined"
                onClick={handleTestToken}
                disabled={testingToken || !deviceToken.trim() || sending}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  borderRadius: '5px',
                  borderColor: '#e6e6e6',
                  color: '#666666',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    backgroundColor: 'rgba(14, 77, 148, 0.04)',
                  },
                }}
              >
                {testingToken ? 'Test Ediliyor...' : 'Token Test Et'}
              </Button>
            </Box>
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button
            onClick={resetForm}
            disabled={sending}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Temizle
          </Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {sending ? 'Gönderiliyor...' : 'Bildirim Gönder'}
          </Button>
        </Stack>
      </Stack>

      {/* Test Token Result Dialog */}
      <Dialog open={testTokenDialogOpen} onClose={() => setTestTokenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Device Token Test Sonucu
          <IconButton
            onClick={() => setTestTokenDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {testTokenResult?.data && (
            <Stack spacing={2}>
              <Alert severity={testTokenResult.data.isValidFormat ? 'success' : 'warning'}>
                {testTokenResult.message || 'Token test edildi.'}
              </Alert>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                  Token Formatı
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1.5, borderRadius: '5px', fontSize: '13px', color: '#444444' }}>
                  {testTokenResult.data.normalizedToken || deviceToken}
                </Typography>
              </Box>
              {testTokenResult.data.recommendations && testTokenResult.data.recommendations.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                    Öneriler
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {testTokenResult.data.recommendations.map((rec, idx) => (
                      <li key={idx}>
                        <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                          {rec}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setTestTokenDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PushNotificationSendTab;

